import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ForumPost as ForumPostType, ForumReply, CreateForumReplyData, ModeratorAction } from '../types';
import { apiClient } from '../services/apiClient';
import { LoadingSpinner } from './LoadingSpinner';
import { useAuth } from '../hooks/useAuth';
import { getRoleDisplay, getRoleBadgeClass, getGenderedRoleLabel, canModerate } from '../utils/roles';
import { getMedicalRoleLabel } from '../utils/medicalRoles';
import { RichTextEditor, SafeHtmlViewer } from './RichTextEditor';

export function ForumPost() {
  const { postId } = useParams<{ postId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [post, setPost] = useState<ForumPostType | null>(null);
  const [replies, setReplies] = useState<ForumReply[]>([]);
  const [loading, setLoading] = useState(true);
  const [repliesLoading, setRepliesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [replyHtml, setReplyHtml] = useState('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [submittingReply, setSubmittingReply] = useState(false);
  const [useRichEditor, setUseRichEditor] = useState(true);
  const [editingPost, setEditingPost] = useState(false);
  const [editPostTitle, setEditPostTitle] = useState('');
  const [editPostContent, setEditPostContent] = useState('');
  const [editPostHtml, setEditPostHtml] = useState('');
  const [editingReply, setEditingReply] = useState<string | null>(null);
  const [editReplyContent, setEditReplyContent] = useState('');
  const [editReplyHtml, setEditReplyHtml] = useState('');

  useEffect(() => {
    if (postId) {
      fetchPost();
      fetchReplies();
    }
  }, [postId]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiClient.get(`/forum?path=posts/${postId}`);
      
      if (response.data.success) {
        setPost(response.data.data);
      } else {
        setError(response.data.message);
      }
    } catch (err: any) {
      console.error('Error fetching post:', err);
      setError('Σφάλμα κατά τη φόρτωση της δημοσίευσης');
    } finally {
      setLoading(false);
    }
  };

  const fetchReplies = async () => {
    try {
      setRepliesLoading(true);
      
      const response = await apiClient.get(`/forum?path=posts/${postId}/replies`);
      
      if (response.data.success) {
        setReplies(response.data.data.replies);
      }
    } catch (err) {
      console.error('Error fetching replies:', err);
    } finally {
      setRepliesLoading(false);
    }
  };

  const handleSubmitReply = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      navigate('/login');
      return;
    }

    const contentToSubmit = useRichEditor ? replyHtml : replyText;
    const plainText = useRichEditor ? replyHtml.replace(/<[^>]*>/g, '').trim() : replyText.trim();
    
    if (!plainText) return;

    try {
      setSubmittingReply(true);
      
      const replyData: CreateForumReplyData = {
        content: plainText,
        content_html: useRichEditor ? replyHtml : undefined,
        parent_reply_id: replyingTo || undefined
      };

      const response = await apiClient.post(
        `/forum?path=posts/${postId}/replies`,
        replyData
      );

      if (response.data.success) {
        setReplyText('');
        setReplyHtml('');
        setReplyingTo(null);
        await fetchReplies();
        
        // Update reply count in post
        if (post) {
          setPost({ ...post, reply_count: post.reply_count + 1 });
        }
      } else {
        alert(response.data.message);
      }
    } catch (err: any) {
      console.error('Error submitting reply:', err);
      alert('Σφάλμα κατά την υποβολή της απάντησης');
    } finally {
      setSubmittingReply(false);
    }
  };

  const handleModeratorAction = async (action: ModeratorAction) => {
    if (!user || !canModerate(user.role)) return;

    try {
      const response = await apiClient.put(
        `/forum?path=posts/${postId}/moderate`,
        action
      );

      if (response.data.success) {
        // Preserve all existing post data and only update the moderated fields
        setPost(prevPost => {
          if (!prevPost) return response.data.data;
          
          return {
            ...prevPost,
            is_pinned: response.data.data.is_pinned,
            is_locked: response.data.data.is_locked,
            updated_at: response.data.data.updated_at
          };
        });
      } else {
        alert(response.data.message);
      }
    } catch (err: any) {
      console.error('Error with moderator action:', err);
      alert('Σφάλμα κατά την εκτέλεση της ενέργειας');
    }
  };

  const handleDeletePost = async () => {
    if (!user || (!canModerate(user.role) && user.id !== post?.user_id)) return;
    
    if (!confirm('Είστε σίγουροι ότι θέλετε να διαγράψετε αυτή τη δημοσίευση;')) {
      return;
    }

    try {
      const response = await apiClient.delete(`/forum?path=posts/${postId}`);
      
      if (response.data.success) {
        navigate(`/forum/category/${post?.category_id}`);
      } else {
        alert(response.data.message);
      }
    } catch (err: any) {
      console.error('Error deleting post:', err);
      alert('Σφάλμα κατά τη διαγραφή της δημοσίευσης');
    }
  };

  const handleDeleteReply = async (replyId: string) => {
    const reply = replies.find(r => r.id === replyId);
    if (!user || (!canModerate(user.role) && user.id !== reply?.user_id)) return;
    
    if (!confirm('Είστε σίγουροι ότι θέλετε να διαγράψετε αυτή την απάντηση;')) {
      return;
    }

    try {
      const response = await apiClient.delete(`/forum?path=posts/${postId}/replies/${replyId}`);
      
      if (response.data.success) {
        await fetchReplies();
        if (post) {
          setPost({ ...post, reply_count: Math.max(0, post.reply_count - 1) });
        }
      } else {
        alert(response.data.message);
      }
    } catch (err: any) {
      console.error('Error deleting reply:', err);
      alert('Σφάλμα κατά τη διαγραφή της απάντησης');
    }
  };

  const handleEditPost = () => {
    if (!post) return;
    setEditingPost(true);
    setEditPostTitle(post.title);
    setEditPostContent(post.content);
    setEditPostHtml(post.content_html || '');
  };

  const handleSavePostEdit = async () => {
    if (!post) return;
    
    const contentToSubmit = useRichEditor ? editPostHtml : editPostContent;
    const plainText = useRichEditor ? editPostHtml.replace(/<[^>]*>/g, '').trim() : editPostContent.trim();
    
    if (!editPostTitle.trim() || !plainText) {
      alert('Τίτλος και περιεχόμενο είναι υποχρεωτικά');
      return;
    }

    try {
      const response = await apiClient.put(`/forum?path=posts/${postId}/edit`, {
        title: editPostTitle,
        content: plainText,
        content_html: useRichEditor ? editPostHtml : undefined
      });

      if (response.data.success) {
        setPost(response.data.data);
        setEditingPost(false);
        setEditPostTitle('');
        setEditPostContent('');
        setEditPostHtml('');
      } else {
        alert(response.data.message);
      }
    } catch (err: any) {
      console.error('Error editing post:', err);
      alert('Σφάλμα κατά την επεξεργασία της δημοσίευσης');
    }
  };

  const handleCancelPostEdit = () => {
    setEditingPost(false);
    setEditPostTitle('');
    setEditPostContent('');
    setEditPostHtml('');
  };

  const handleEditReply = (reply: ForumReply) => {
    setEditingReply(reply.id);
    setEditReplyContent(reply.content);
    setEditReplyHtml(reply.content_html || '');
  };

  const handleSaveReplyEdit = async (replyId: string) => {
    const contentToSubmit = useRichEditor ? editReplyHtml : editReplyContent;
    const plainText = useRichEditor ? editReplyHtml.replace(/<[^>]*>/g, '').trim() : editReplyContent.trim();
    
    if (!plainText) {
      alert('Το περιεχόμενο είναι υποχρεωτικό');
      return;
    }

    try {
      const response = await apiClient.put(`/forum?path=posts/${postId}/replies/${replyId}/edit`, {
        content: plainText,
        content_html: useRichEditor ? editReplyHtml : undefined
      });

      if (response.data.success) {
        await fetchReplies();
        setEditingReply(null);
        setEditReplyContent('');
        setEditReplyHtml('');
      } else {
        alert(response.data.message);
      }
    } catch (err: any) {
      console.error('Error editing reply:', err);
      alert('Σφάλμα κατά την επεξεργασία της απάντησης');
    }
  };

  const handleCancelReplyEdit = () => {
    setEditingReply(null);
    setEditReplyContent('');
    setEditReplyHtml('');
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 60) {
      return `${minutes} λεπτά πριν`;
    } else if (hours < 24) {
      return `${hours} ώρες πριν`;
    } else if (days < 7) {
      return `${days} μέρες πριν`;
    } else {
      return date.toLocaleDateString('el-GR');
    }
  };

  const renderReply = (reply: ForumReply, depth: number = 0) => {
    const maxDepth = 5;
    const isNested = depth > 0;
    const canNestMore = depth < maxDepth;

    return (
      <div
        key={reply.id}
        className={`${isNested ? 'ml-8 border-l-2 border-gray-200 pl-4' : ''} ${
          depth === 0 ? 'border-b border-gray-200 pb-6 mb-6' : 'mb-4'
        }`}
      >
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          {/* Reply Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-blue-800">
                    {reply.author_name?.charAt(0) || 'U'}
                  </span>
                </div>
              </div>
              <div>
                <div className="flex items-center space-x-2">
                  <span className="font-medium text-gray-900">{reply.author_name}</span>
                  {reply.author_role && (
                    <span className={getRoleBadgeClass(reply.author_role)}>
                      {getGenderedRoleLabel(reply.author_role, reply.author_sex)}
                    </span>
                  )}
                  {reply.author_medical_role && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {getMedicalRoleLabel(reply.author_medical_role, reply.author_sex)}
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-500">
                  {formatTimeAgo(reply.created_at)}
                  {reply.is_edited && (
                    <span className="ml-2 text-xs text-gray-400">(επεξεργασμένη)</span>
                  )}
                </div>
              </div>
            </div>

            {/* Reply Actions */}
            <div className="flex items-center space-x-2">
              {user && !post?.is_locked && canNestMore && (
                <button
                  onClick={() => setReplyingTo(reply.id)}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Απάντηση
                </button>
              )}
              {user && (canModerate(user.role) || user.id === reply.user_id) && (
                <>
                  <button
                    onClick={() => handleEditReply(reply)}
                    className="text-sm text-green-600 hover:text-green-800"
                  >
                    Επεξεργασία
                  </button>
                  <button
                    onClick={() => handleDeleteReply(reply.id)}
                    className="text-sm text-red-600 hover:text-red-800"
                  >
                    Διαγραφή
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Reply Content */}
          <div className="prose prose-sm max-w-none">
            {editingReply === reply.id ? (
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Επεξεργασία απάντησης
                    </label>
                    <button
                      type="button"
                      onClick={() => setUseRichEditor(!useRichEditor)}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      {useRichEditor ? 'Απλό κείμενο' : 'Μορφοποίηση'}
                    </button>
                  </div>
                  {useRichEditor ? (
                    <RichTextEditor
                      value={editReplyHtml}
                      onChange={setEditReplyHtml}
                      placeholder="Επεξεργαστείτε την απάντησή σας..."
                      height={200}
                      mode="basic"
                    />
                  ) : (
                    <textarea
                      value={editReplyContent}
                      onChange={(e) => setEditReplyContent(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      rows={5}
                      placeholder="Επεξεργαστείτε την απάντησή σας..."
                    />
                  )}
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={handleCancelReplyEdit}
                    className="px-3 py-1 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                  >
                    Ακύρωση
                  </button>
                  <button
                    onClick={() => handleSaveReplyEdit(reply.id)}
                    className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Αποθήκευση
                  </button>
                </div>
              </div>
            ) : (
              <>
                {reply.content_html ? (
                  <SafeHtmlViewer html={reply.content_html} />
                ) : (
                  <div dangerouslySetInnerHTML={{ __html: reply.content.replace(/\n/g, '<br>') }} />
                )}
                {reply.is_edited && (
                  <p className="text-xs text-gray-500 mt-1 italic">
                    (επεξεργασμένη)
                  </p>
                )}
              </>
            )}
          </div>

          {/* Reply Form for this specific reply */}
          {replyingTo === reply.id && user && (
            <form onSubmit={handleSubmitReply} className="mt-4 pt-4 border-t border-gray-200">
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder={`Απάντηση στον/στη ${reply.author_name}...`}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                rows={3}
                disabled={submittingReply}
              />
              <div className="flex justify-between items-center mt-2">
                <button
                  type="button"
                  onClick={() => {
                    setReplyingTo(null);
                    setReplyText('');
                  }}
                  className="text-sm text-gray-600 hover:text-gray-800"
                >
                  Ακύρωση
                </button>
                <button
                  type="submit"
                  disabled={!replyText.trim() || submittingReply}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submittingReply ? 'Αποστολή...' : 'Απάντηση'}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Nested Replies */}
        {reply.replies && reply.replies.length > 0 && (
          <div className="mt-4">
            {reply.replies.map(nestedReply => renderReply(nestedReply, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  if (!postId) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Δεν βρέθηκε η δημοσίευση</p>
        <Link to="/forum" className="text-blue-600 hover:text-blue-800">
          Επιστροφή στο φόρουμ
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-800 mb-4">{error || 'Η δημοσίευση δεν βρέθηκε'}</p>
        <div className="space-x-4">
          <button
            onClick={fetchPost}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
          >
            Δοκιμάστε ξανά
          </button>
          <Link
            to="/forum"
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
          >
            Επιστροφή στο φόρουμ
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Breadcrumb */}
      <nav className="flex mb-6" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-3">
          <li className="inline-flex items-center">
            <Link to="/forum" className="text-gray-700 hover:text-blue-600">
              Φόρουμ
            </Link>
          </li>
          <li>
            <div className="flex items-center">
              <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"></path>
              </svg>
              <Link 
                to={`/forum/category/${post.category_id}`}
                className="ml-1 md:ml-2 text-gray-700 hover:text-blue-600"
              >
                {post.category_name}
              </Link>
            </div>
          </li>
          <li>
            <div className="flex items-center">
              <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"></path>
              </svg>
              <span className="ml-1 md:ml-2 text-gray-500 truncate max-w-xs">
                {post.title}
              </span>
            </div>
          </li>
        </ol>
      </nav>

      {/* Post */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-8">
        {/* Post Header */}
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              {/* Post Status Badges */}
              <div className="flex items-center space-x-2 mb-2">
                {post.is_pinned && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                    </svg>
                    Καρφιτσωμένη
                  </span>
                )}
                {post.is_locked && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                    Κλειδωμένη
                  </span>
                )}
              </div>

              <h1 className="text-2xl font-bold text-gray-900 mb-4">{post.title}</h1>

              {/* Author Info */}
              <div className="flex items-center space-x-4 text-sm text-gray-600">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-blue-800">
                      {post.author_name?.charAt(0) || 'U'}
                    </span>
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-gray-900">{post.author_name}</span>
                      {post.author_role && (
                        <span className={getRoleBadgeClass(post.author_role)}>
                          {getGenderedRoleLabel(post.author_role, post.author_sex)}
                        </span>
                      )}
                      {post.author_medical_role && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {getMedicalRoleLabel(post.author_medical_role, post.author_sex)}
                        </span>
                      )}
                    </div>
                    {post.author_specialty && (
                      <div className="text-xs text-gray-500">{post.author_specialty}</div>
                    )}
                  </div>
                </div>
                <div className="text-gray-400">•</div>
                <span>{formatTimeAgo(post.created_at)}</span>
                <div className="text-gray-400">•</div>
                <span>{post.view_count} προβολές</span>
              </div>
            </div>

            {/* Post Actions */}
            <div className="flex items-center space-x-2">
              {/* Moderator Actions */}
              {user && canModerate(user.role) && (
                <>
                  <button
                    onClick={() => handleModeratorAction({ action: 'pin', value: !post.is_pinned })}
                    className={`px-3 py-1 text-xs rounded ${
                      post.is_pinned
                        ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                        : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                    }`}
                  >
                    {post.is_pinned ? 'Ξεκαρφίτσωμα' : 'Καρφίτσωμα'}
                  </button>
                  <button
                    onClick={() => handleModeratorAction({ action: 'lock', value: !post.is_locked })}
                    className={`px-3 py-1 text-xs rounded ${
                      post.is_locked
                        ? 'bg-red-100 text-red-800 hover:bg-red-200'
                        : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                    }`}
                  >
                    {post.is_locked ? 'Ξεκλείδωμα' : 'Κλείδωμα'}
                  </button>
                </>
              )}
              
              {/* Author actions - edit and delete */}
              {user && (canModerate(user.role) || user.id === post.user_id) && (
                <>
                  <button
                    onClick={handleEditPost}
                    className="px-3 py-1 text-xs bg-blue-100 text-blue-800 hover:bg-blue-200 rounded"
                  >
                    Επεξεργασία
                  </button>
                  <button
                    onClick={handleDeletePost}
                    className="px-3 py-1 text-xs bg-red-100 text-red-800 hover:bg-red-200 rounded"
                  >
                    Διαγραφή
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Post Content */}
        <div className="px-6 py-6">
          {editingPost ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Τίτλος
                </label>
                <input
                  type="text"
                  value={editPostTitle}
                  onChange={(e) => setEditPostTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Τίτλος της δημοσίευσης"
                />
              </div>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Περιεχόμενο
                  </label>
                  <button
                    type="button"
                    onClick={() => setUseRichEditor(!useRichEditor)}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    {useRichEditor ? 'Απλό κείμενο' : 'Μορφοποίηση'}
                  </button>
                </div>
                {useRichEditor ? (
                  <RichTextEditor
                    value={editPostHtml}
                    onChange={setEditPostHtml}
                    placeholder="Επεξεργαστείτε το περιεχόμενο της δημοσίευσης..."
                    height={300}
                    mode="full"
                  />
                ) : (
                  <textarea
                    value={editPostContent}
                    onChange={(e) => setEditPostContent(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    rows={10}
                    placeholder="Επεξεργαστείτε το περιεχόμενο της δημοσίευσης..."
                  />
                )}
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleCancelPostEdit}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                >
                  Ακύρωση
                </button>
                <button
                  onClick={handleSavePostEdit}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Αποθήκευση
                </button>
              </div>
            </div>
          ) : (
            <div className="prose max-w-none">
              {post.content_html ? (
                <SafeHtmlViewer html={post.content_html} />
              ) : (
                <div dangerouslySetInnerHTML={{ __html: post.content.replace(/\n/g, '<br>') }} />
              )}
              {post.is_edited && (
                <p className="text-xs text-gray-500 mt-2 italic">
                  (επεξεργασμένη)
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Reply Form */}
      {user && !post.is_locked && !replyingTo && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-8">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Γράψε μια απάντηση</h3>
            <button
              type="button"
              onClick={() => setUseRichEditor(!useRichEditor)}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              {useRichEditor ? 'Απλό κείμενο' : 'Μορφοποίηση'}
            </button>
          </div>
          <form onSubmit={handleSubmitReply}>
            {useRichEditor ? (
              <RichTextEditor
                value={replyHtml}
                onChange={setReplyHtml}
                placeholder="Γράψε την απάντησή σου εδώ..."
                height={200}
                disabled={submittingReply}
                mode="basic"
              />
            ) : (
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Γράψε την απάντησή σου εδώ..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                rows={6}
                disabled={submittingReply}
              />
            )}
            <div className="flex justify-between items-center mt-4">
              <p className="text-sm text-gray-500">
                {useRichEditor && 'Συμβουλή: Χρησιμοποιήστε τα εργαλεία μορφοποίησης για καλύτερη παρουσίαση'}
              </p>
              <button
                type="submit"
                disabled={(
                  useRichEditor 
                    ? !replyHtml.replace(/<[^>]*>/g, '').trim() 
                    : !replyText.trim()
                ) || submittingReply}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submittingReply ? 'Αποστολή...' : 'Δημοσίευση Απάντησης'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Login Prompt */}
      {!user && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
          <div className="text-center">
            <p className="text-blue-800 mb-4">
              Θέλετε να συμμετάσχετε στη συζήτηση;
            </p>
            <Link
              to="/login"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Συνδεθείτε για να απαντήσετε
            </Link>
          </div>
        </div>
      )}

      {/* Replies */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Απαντήσεις ({post.reply_count})
          </h2>
        </div>

        <div className="px-6 py-6">
          {repliesLoading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : replies.length > 0 ? (
            <div className="space-y-6">
              {replies.map(reply => renderReply(reply))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.418 8-9 8a9.013 9.013 0 01-5.314-1.673l-3.563.789a1.5 1.5 0 01-1.777-1.777l.789-3.563A8.963 8.963 0 013 12C3 7.582 7.582 3 12 3s9 3.582 9 9z" />
              </svg>
              <p className="mt-4 text-lg">Δεν υπάρχουν απαντήσεις ακόμα</p>
              <p className="text-sm">
                {user && !post.is_locked ? 
                  'Γίνετε ο πρώτος που θα απαντήσει!' :
                  'Συνδεθείτε για να γράψετε την πρώτη απάντηση'
                }
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}